<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import InputNumber from 'primevue/inputnumber';
import Message from 'primevue/message';
import { computed, useAttrs, type Ref } from 'vue';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpNumberFieldProps = {
    label?: string;
    inputId?: string;
    inputClass?: string | object;
    error?: string | null;
};

const value: Ref<any> = defineModel();
const props = withDefaults(defineProps<KpNumberFieldProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    error: '',
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
    (e: 'enter'): void;
}>();

const cls = useAttrs().class || null;
const fieldAttrs = computed(() => {
    const returnObj = { ...useAttrs() };
    // Remove attributes for the inner field we do NOT want to pass along:
    delete returnObj.id;
    delete returnObj.class;
    delete returnObj.label;
    return returnObj;
});

function onKeypress(e: KeyboardEvent) {
    if (e.key.toLowerCase() === 'enter') {
        emit('enter');
    }
}

function onChange(value: any) {
    emit('change', value);
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <InputNumber
            :id="inputId"
            v-model="value"
            :class="inputClass"
            locale="de-CH"
            v-bind="fieldAttrs"
            @keypress="onKeypress"
            @value-change="onChange"
        />
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
